<template>
  <div class="tab-content">
    <div class="member-type-selector " v-if="hasAnyData">
      <template v-for="(category, index) in digitalDataArray" :key="category.title">
        <div class="member-type-label" @click="digitalHumanType = categoryTypes[index] || ''"
          v-if="category.list && category.list.length > 0">
          <span class="radio-circle" :class="{ active: digitalHumanType === categoryTypes[index] }"></span>
          <span class="radio-text">{{ category.title }}</span>
        </div>
      </template>
    </div>
    <!-- 没有数据时显示Empty组件 -->
    <Empty v-if="!hasAnyData" :text="$t('digitalHumanRecharge.noData')" />
    <!-- 统一的卡片布局 -->
    <div v-else :class="currentPackages.length>3?'package-cards':'package-cards-center'">
      <div v-for="pkg in currentPackages" :key="pkg.id" class="price-card"
        :class="{ 'popular-card': pkg.tag === 'popular' }">
        <div v-if="pkg.tag" class="card-tag" :class="pkg.tag">
          {{ getTagText(pkg.tag) }}
        </div>
        <div class="card-header">
          <h3>{{ pkg.name }}</h3>
          <div class="price-container">
            <div class="current-price">
              <span class="price-symbol">{{ pkg.coinType }}</span>
              <span class="price-value">{{ pkg.price }}</span>
              <span class="price-period">/{{ pkg.period }}</span>
            </div>
            <div v-if="pkg.originalPrice" class="original-price">
              {{ pkg.coinType }}{{ pkg.originalPrice }}
            </div>
          </div>
        </div>
        <div class="action-btn">
          <button @click.stop="openPay ? goToDigitalAssets() : handleContactBusiness()" class="buy-now-btn">
            {{
              openPay ? $t('digitalHumanRecharge.buyNow') : $t('partnerRecharge.contactBusiness')
            }}
          </button>
        </div>
        <div class="features-list">
          <div v-for="(feature, idx) in pkg.features" :key="idx" class="feature-item">
            <span class="feature-text" v-if="!feature.html">
              {{ feature.text }}
            </span>
            <span class="feature-text" v-else v-html="feature.text"></span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- 联系商务弹窗 -->
  <ContactDialog ref="serviceDialogRef" />
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { currencySymbolArr } from '@/config'
import Empty from '@/components/empty/Empty.vue'
import { useSystemConfigStoreHook } from '@/store/modules/systemConfig';
import { useI18n } from 'vue-i18n'
import type { GlobalStateEnum } from '@/config'
import { goToDigitalAssets } from '@/utils/redirect';
// 使用国际化
const { t } = useI18n()
const TYPE_IMAGE = 'image'
const TYPE_DURATION = 'duration'
const TYPE_INTERACTIVE = 'interactive'
const TYPE_LIVE = 'live'
const TYPE_RESOURCE = 'resource'
const serviceDialogRef=ref();
// 定义类别类型数组
const categoryTypes = [TYPE_IMAGE, TYPE_DURATION, TYPE_INTERACTIVE, TYPE_LIVE, TYPE_RESOURCE]

const emit = defineEmits(['recharge'])

// 获取系统配置store
const systemConfigStore = useSystemConfigStoreHook()

// 从系统配置中获取openPay状态
const openPay = computed(() => systemConfigStore.toolStationConfig?.openPay ?? true)

// 打开联系商务弹窗
const handleContactBusiness = () => {
  serviceDialogRef.value.showServiceDialog();
}

// 定义接口
interface DigitalHumanCategory {
  title: string
  list: Array<{
    id: number
    title: string
    price: number
    discountPrice: number
    desc: string[]
    tag: string | null
    enableDiscount: GlobalStateEnum
    enableRecharge: number
    monthCount: number
  }>
}

interface PackageItem {
  id: number
  name: string
  price: number
  period: string
  originalPrice: number | undefined
  coinType: string
  features: Array<{
    text: string
    html: boolean
  }>
  enableDiscount: GlobalStateEnum
  tag?: string
  enableRecharge: boolean
}

interface CategoryMap {
  image: any[]
  duration: any[]
  interactive: any[]
  live: any[]
  resource: any[]
}

const props = defineProps({
  digitalData: {
    type: Array as () => DigitalHumanCategory[],
    default: () => []
  },
  currency: {
    type: String,
    default: 'CNY'
  }
})

// 数字人子类型
const digitalHumanType = ref<string>(TYPE_IMAGE)

// 获取货币符号
const getCurrencySymbol = (currency: string): string => {
  const currencyItem = currencySymbolArr.find(item => item.name === currency)
  return currencyItem ? currencyItem.value : '¥'
}

// 计算不同类型的数据
const digitalHumanCategories = computed<CategoryMap>(() => {
  console.log('处理数字人数据:', props.digitalData)

  if (!props.digitalData || !Array.isArray(props.digitalData) || props.digitalData.length === 0) {
    console.warn('数字人数据为空或格式不正确')
    return {
      image: [],
      duration: [],
      interactive: [],
      live: [],
      resource: []
    }
  }

  // 不再通过固定title查找，而是通过索引位置
  return {
    // 索引0：形象版
    image: props.digitalData?.length > 0 ? props.digitalData[0]?.list : [],
    // 索引1：时长版
    duration: props.digitalData?.length > 1 ? props.digitalData[1]?.list : [],
    // 索引2：互动版
    interactive: props.digitalData?.length > 2 ? props.digitalData[2]?.list : [],
    // 索引3：直播版
    live: props.digitalData?.length > 3 ? props.digitalData[3]?.list : [],
    // 索引4：资源包
    resource: props.digitalData?.length > 4 ? props.digitalData[4]?.list : []
  }
})

// 检查各类数据是否为空
const hasImageData = computed(
  () => digitalHumanCategories.value.image && digitalHumanCategories.value.image.length > 0
)
const hasDurationData = computed(
  () => digitalHumanCategories.value.duration && digitalHumanCategories.value.duration.length > 0
)
const hasInteractiveData = computed(
  () =>
    digitalHumanCategories.value.interactive &&
    digitalHumanCategories.value.interactive.length > 0
)
const hasLiveData = computed(
  () => digitalHumanCategories.value.live && digitalHumanCategories.value.live.length > 0
)
const hasResourceData = computed(
  () => digitalHumanCategories.value.resource && digitalHumanCategories.value.resource.length > 0
)

// 检查是否有任何数据
const hasAnyData = computed(() => {
  console.log('digitalHuman数据检查:', props.digitalData)
  return (
    hasImageData.value ||
    hasDurationData.value ||
    hasInteractiveData.value ||
    hasLiveData.value ||
    hasResourceData.value
  )
})

// 监听数据变化，自动选择第一个有数据的类型
watch(
  () => props.digitalData,
  () => {
    // 顺序检查各种类型的数据，选择第一个有数据的类型
    if (hasImageData.value) {
      digitalHumanType.value = TYPE_IMAGE
    } else if (hasDurationData.value) {
      digitalHumanType.value = TYPE_DURATION
    } else if (hasInteractiveData.value) {
      digitalHumanType.value = TYPE_INTERACTIVE
    } else if (hasLiveData.value) {
      digitalHumanType.value = TYPE_LIVE
    } else if (hasResourceData.value) {
      digitalHumanType.value = TYPE_RESOURCE
    }
  },
  { immediate: true }
)

// 将API数据转换为组件需要的格式
const convertApiData = (data: any[]): PackageItem[] => {
  return data.map(item => ({
    id: item.id,
    name: item.title,
    // 始终使用折扣价作为主要价格
    price: item.discountPrice,
    // 根据monthCount显示月份，-1表示永久 遗留 
    period: item.monthCount === -1
      ? t('digitalHumanRecharge.permanent')
      : item.monthCount
        ? t('digitalHumanRecharge.months', { count: item.monthCount })
        : t('digitalHumanRecharge.defaultPeriod'),
    // 始终显示原价作为划线价
    originalPrice: item.price,
    enableDiscount: item.enableDiscount,
    coinType: getCurrencySymbol(props.currency),
    features: item.desc?.map((text: string) => ({
      text: text,
      html: text.includes('<') // 如果包含HTML标签
    })),
    // 直接使用tag值
    tag: item.tag && item.tag !== 'none' ? item.tag : undefined,
    enableRecharge: item.enableRecharge === 1
  }))
}

// 显示标签文本
const getTagText = (tag: string): string => {
  switch (tag) {
    case 'popular':
      return t('digitalHumanRecharge.tags.popular')
    case 'value':
      return t('digitalHumanRecharge.tags.value')
    default:
      return ''
  }
}

// 类别数组
const digitalDataArray = computed<DigitalHumanCategory[]>(() => {
  return props.digitalData || []
})

// 根据当前选中的类型获取对应的套餐数据
const currentPackages = computed<PackageItem[]>(() => {
  switch (digitalHumanType.value) {
    case TYPE_IMAGE:
      return convertApiData(digitalHumanCategories.value.image)
    case TYPE_DURATION:
      return convertApiData(digitalHumanCategories.value.duration)
    case TYPE_INTERACTIVE:
      return convertApiData(digitalHumanCategories.value.interactive)
    case TYPE_LIVE:
      return convertApiData(digitalHumanCategories.value.live)
    case TYPE_RESOURCE:
      return convertApiData(digitalHumanCategories.value.resource)
    default:
      return []
  }
})
</script>
<style scoped>
/* 会员类型选择器样式 */
.member-type-selector {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin: 10px 0 25px;
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  max-width: 100%;
  flex-wrap: wrap;
  margin-left: 0;
  margin-right: auto;
}

.member-type-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 20px;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  white-space: nowrap;
}

.member-type-label:hover {
  background-color: #f0f7ff;
}

.radio-circle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #ddd;
  margin-right: 8px;
  position: relative;
  flex-shrink: 0;
}

.radio-circle.active {
  border-color: var(--el-color-primary);
}

.radio-circle.active:after {
  content: '';
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--el-color-primary);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.radio-text {
  font-size: 14px;
}

.digital-human-types {
  margin: 20px 0;
}

.type-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

/* 形象版卡片布局样式 */
.package-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, calc(25% - 15px)));
  gap: 20px;
  margin: 0 0 30px;
}
.package-cards-center{
  display: flex;
  flex-direction: row;
   gap: 10px;
   flex-wrap: wrap;
   justify-content: center;
}

.price-card {
  border-radius: 12px;
  border: 1px solid #ebeef5;
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.08);
  padding: 0;
  background-color: #fff;
  position: relative;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin: 10px 5px;
  max-width: 380px;
  /* 限制卡片最大宽度 */
  width: 100%;
  justify-self: start;
  /* 使卡片在grid中居左对齐 */
}

.price-card:hover {
  transform: translateY(-10px) scale(1.02);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  border-color: rgba(var(--el-color-primary-rgb), 0.3);
  z-index: 1;
}

.popular-card {
  /* border: 2px solid var(--el-color-primary); */
  /* box-shadow: 0 6px 20px rgba(var(--el-color-primary-rgb), 0.25); */
  transform: translateY(-5px);
}

.card-tag {
  position: absolute;
  top: 10px;
  right: -30px;
  transform: rotate(45deg);
  padding: 5px 30px;
  font-size: 12px;
  font-weight: bold;
  color: white;
  text-align: center;
}

.card-tag.popular {
  background-color: var(--el-color-primary);
}

.card-tag.value {
  background-color: #000;
}

.card-header {
  padding: 25px 20px;
  text-align: center;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fafafa;
}

.card-header h3 {
  margin: 0 0 15px;
  font-size: 20px;
  font-weight: bold;
  color: #333;
}

.price-container {
  margin: 15px 0;
  text-align: center;
}

.current-price {
  margin-bottom: 8px;
  display: flex;
  align-items: baseline;
  justify-content: center;
}

.price-symbol {
  font-size: 20px;
  color: #333;
}

.price-value {
  font-size: 40px;
  font-weight: bold;
  margin: 0 5px;
  color: #333;
  line-height: 1;
}

.price-period {
  font-size: 14px;
  color: #909399;
  margin-left: 2px;
}

.original-price {
  font-size: 16px;
  color: #909399;
  text-decoration: line-through;
  opacity: 0.8;
}

.price-description {
  font-size: 14px;
  color: #606266;
  margin: 0;
}

.features-list {
  padding: 20px 20px;
  flex-grow: 1;
  background-color: #fff;
}

.feature-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  margin: 6px 0;
  border-bottom: none;
}

.feature-item:last-child {
  border-bottom: none;
  margin-bottom: 10px;
}

.feature-text {
  font-size: 14px;
  color: #606266;
  line-height: 1.5;
  padding-left: 8px;
  border-left: 3px solid var(--el-color-primary);
}

.feature-text.highlight {
  color: #606266;
  font-weight: normal;
}

.feature-check {
  color: #67c23a;
  font-weight: bold;
}

.feature-cross {
  color: #909399;
  font-weight: bold;
}

.contact-us {
  font-size: 28px;
  color: #722ed1;
  font-weight: bold;
  margin-bottom: 5px;
}

.enterprise-card {
  background-color: #fff;
  position: relative;
  overflow: hidden;
}

.enterprise-card:before {
  content: '';
  position: absolute;
  top: -20px;
  right: -20px;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, transparent 30%, rgba(114, 46, 209, 0.1) 100%);
  border-radius: 0 0 0 80px;
  z-index: 0;
}

.enterprise-card .card-header {
  position: relative;
  z-index: 1;
}

.enterprise-card .features-list .feature-item {
  border-bottom: 1px dashed rgba(114, 46, 209, 0.2);
}

.enterprise-card .features-list .feature-text {
  font-weight: 500;
}

/* 其他选项卡的样式 */
.package-group-container {
  margin: 30px 0;
}

.package-group {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 15px;
}

.package-option {
  flex: 1;
  min-width: 150px;
  height: auto;
  background-color: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
}

.package-option:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.package-content {
  padding: 10px 0;
  text-align: center;
}

.package-name {
  font-weight: bold;
  margin-bottom: 5px;
}

.package-price {
  font-size: 20px;
  color: #f56c6c;
  margin-bottom: 5px;
}

.package-description {
  font-size: 12px;
  color: #909399;
  margin-bottom: 10px;
}

.radio-action-btn {
  padding: 10px 0;
  text-align: center;
}

.action-btn {
  padding: 15px 20px;
  text-align: center;
}

.contact-btn {
  width: 100%;
  padding: 10px 0;
  border: none;
  border-radius: 20px;
  background: linear-gradient(to right, var(--color-gradient-start), var(--el-color-primary));
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
}

.contact-btn:hover {
  opacity: 0.9;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(var(--el-color-primary-rgb), 0.3);
}

.buy-now-btn {
  width: 100%;
  padding: 10px 0;
  border: none;
  border-radius: 20px;
  background: linear-gradient(to right, var(--color-gradient-start), var(--el-color-primary));
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
}

.buy-now-btn:hover {
  opacity: 0.95;
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(var(--el-color-primary-rgb), 0.4);
}

.buy-now-btn:active {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(var(--el-color-primary-rgb), 0.3);
}

.buy-now-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: all 0.6s;
}

.buy-now-btn:hover::before {
  left: 100%;
}
</style>
